import castErrorToBaseError from '@/functions/castErrorToBaseError';
import config from '@/modules/config/functions/config';
import updateLockStore from '@/modules/core/stores/update-lock';
import PromotionDeleteModal from '@/modules/promotions/components/promotion-delete-modal';
import usePromotionsQuery from '@/modules/promotions/queries/usePromotionsQuery';
import type { Promotion } from '@/modules/promotions/types/Promotion';
import { Helmet } from '@dr.pogodin/react-helmet';
import deLocale from '@fullcalendar/core/locales/de';
import dayGridPlugin from '@fullcalendar/daygrid';
import FullCalendar from '@fullcalendar/react';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Button,
  Dropdown,
  Pagination,
  Skeleton,
  Table,
  Typography,
  useBreakpoint,
  useNotification,
} from '@thisisdmg/tirecloud-patterns/v2';
import {
  IconDeleteTrash,
  IconEdit,
} from '@thisisdmg/tirecloud-patterns/v2/components/icons';
import { type } from 'arktype';
import {
  _unixToClientTime,
  _clientTimeFormat,
  _clientNow,
} from '@/functions/timezone';
import { capitalize } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { match, P } from 'ts-pattern';
import { sortPromotionsByErpCode } from '@/modules/promotions/functions/sortPromotionsByErpCode';
import classes from './index.module.css';
import './react-big-calendar.css';
import useEditPromotionMutation from '@/modules/promotions/mutations/useEditPromotionMutation';
// import promotions from './promotions.mock-data.json' with { type: 'json' };

const PAGE_SIZE = config('misc.dataTablePageSize', 10);

function PromotionPage() {
  const { t, i18n } = useTranslation();
  const navigate = Route.useNavigate();
  const p = Route.useSearch({ select: (s) => s.p });
  const notification = useNotification();
  const [prmotionToBeDeleted, setPromotionToBeDeleted] = useState<
    number | undefined
  >(undefined);
  const editPromotionMutation = useEditPromotionMutation();
  const breakpoint = useBreakpoint();

  const {
    data: promotions,
    isLoading,
    isError,
    error: promotionsQueryError,
  } = usePromotionsQuery({
    variables: {
      query: {
        offset: ((p ?? 1) - 1) * PAGE_SIZE,
        limit: 0,
      },
    },
  });

  const sortedPromotions = useMemo(
    () => (promotions?.data ? sortPromotionsByErpCode(promotions.data) : []),
    [promotions]
  );

  useEffect(() => {
    if (!isError) return;
    const error = castErrorToBaseError(promotionsQueryError);
    notification.error({
      key: error.id,
      message: `0x${error.id.toString(16).toUpperCase()}`,
      description: t(`errors.gen.${error.path}`, { errorId: error.id }),
    });
  }, [isError, notification, promotionsQueryError, t]);

  return (
    <>
      <Helmet>
        <title>{t('common.promotion_other')}</title>
      </Helmet>
      <div className={classes.header}>
        <Typography variant="hero">{t('common.promotion_other')}</Typography>
        <Button.Solid
          tag={Link}
          to="/promotions/create"
          onClick={() => updateLockStore.getState().lockUpdate()}
        >
          {t('promotions.create_promotion')}
        </Button.Solid>
      </div>
      {isLoading ? (
        <Skeleton active />
      ) : (
        <>
          <div className={classes.promotion_calendar}>
            <FullCalendar
              height={Math.round(window.innerHeight / 2)}
              plugins={[dayGridPlugin]}
              // initialView="dayGridMonth"
              events={promotions?.data
                .filter(
                  (promotion) =>
                    promotion.status !== 'deleted' &&
                    promotion.status !== 'disabled'
                )
                .map((promotion) => {
                  const status = promotion.status;
                  const validUntilClient = _unixToClientTime(
                    promotion.validUntil
                  );
                  const isEnded = _clientNow().isAfter(validUntilClient);

                  return {
                    id: promotion.id.toString(),
                    title: `${promotion.name}`,
                    end: validUntilClient.toDate(),
                    start: _unixToClientTime(promotion.validFrom).toDate(),
                    allDay: true,
                    url: `/promotions/${promotion.id}`,
                    textColor: match(status)
                      .with(
                        P.when((x) => !isEnded && x === 'enabled'),
                        () => 'var(--tcvtwo-color-base-black)'
                      )
                      .with(
                        P.when((x) => !isEnded && x === 'planned'),
                        () => 'var(--tcvtwo-color-base-black)'
                      )
                      .otherwise(() => 'var(--tcvtwo-color-base-black)'),
                    color: match(status)
                      .with(
                        P.when((x) => !isEnded && x === 'enabled'),
                        () => 'var(--tcvtwo-color-primary-yellow-500)'
                      )
                      .with(
                        P.when((x) => !isEnded && x === 'planned'),
                        () => 'var(--tcvtwo-color-feedback-blue-200)'
                      )
                      .otherwise(() => 'var(--tcvtwo-color-neutrals-gray-500)'),
                  };
                })}
              eventClick={(info) => {
                info.jsEvent.preventDefault();
                navigate({
                  to: `/promotions/${info.event.id}`,
                });
              }}
              headerToolbar={{
                end: 'prev,next dayGridMonth,dayGridWeek',
              }}
              locales={[deLocale]}
              locale={i18n.language}
            />
          </div>
          <div className={classes.promotion_list}>
            <Table<Promotion>
              className={classes.promotion_list_table}
              dataSource={sortedPromotions}
              rowKey="id"
              scroll={breakpoint.md === true ? undefined : { x: 'max-content' }}
              columns={[
                {
                  title: t('common.name'),
                  dataIndex: 'name',
                  key: 'name',
                  width: '20%',
                  render(_, record) {
                    return (
                      <Link
                        to="/promotions/$id"
                        params={{ id: record.id.toString() }}
                      >
                        {record.name}
                      </Link>
                    );
                  },
                },
                {
                  title: t('common.description'),
                  dataIndex: 'description',
                  key: 'description',
                  width: '20%',
                  render(_, record) {
                    return (
                      <Link
                        to="/promotions/$id"
                        params={{ id: record.id.toString() }}
                      >
                        {record.description}
                      </Link>
                    );
                  },
                },
                {
                  title: t('common.status'),
                  key: 'status',
                  width: '10%',
                  render: (_, record) => {
                    const options: Array<{
                      label: string;
                      value: string;
                      show: boolean;
                    }> = [
                      {
                        label: capitalize(t('common.enabled')),
                        value: 'enabled',
                        show:
                          record.status === 'planned' ||
                          record.status === 'disabled',
                      },
                      {
                        label: capitalize(t('common.disabled')),
                        value: 'disabled',
                        show:
                          record.status === 'enabled' ||
                          record.status === 'planned',
                      },
                    ].filter((option) => option.show);

                    return (
                      <Dropdown
                        menu={{
                          items: options.map((option) => ({
                            label: option.label,
                            key: option.value,
                            type: 'item',
                            onClick: async () => {
                              updateLockStore.getState().lockUpdate();
                              try {
                                await editPromotionMutation.mutateAsync({
                                  id: record.id,
                                  data: {
                                    status: option.value as Promotion['status'],
                                  },
                                });
                              } finally {
                                updateLockStore.getState().unlockUpdate();
                              }
                            },
                          })),
                        }}
                        disabled={options.length === 0}
                        key={record.status}
                      >
                        {capitalize(record.status)}
                      </Dropdown>
                    );
                  },
                },
                {
                  title: t('common.valid_from'),
                  key: 'validFrom',
                  width: '10%',
                  render: (_, record) => {
                    return _clientTimeFormat(
                      record.validFrom,
                      'YYYY-MM-DD HH:mm'
                    );
                  },
                },
                {
                  title: t('common.valid_until'),
                  key: 'validUntil',
                  width: '10%',
                  render: (_, record) => {
                    return _clientTimeFormat(
                      record.validUntil,
                      'YYYY-MM-DD HH:mm'
                    );
                  },
                },
                {
                  title: t('common.actions'),
                  key: 'actions',
                  width: '30%',
                  render: (_, record) => {
                    return (
                      <div className={classes.promotion_list_table_actions}>
                        {!['deleted', 'disabled'].includes(record.status) && (
                          <Button.Solid
                            tag={Link}
                            to={`/promotions/edit/${record.id}`}
                            icon={<IconEdit />}
                            data-action="edit"
                            onClick={() =>
                              updateLockStore.getState().lockUpdate()
                            }
                          >
                            {t('common.edit')}
                          </Button.Solid>
                        )}
                        <Button.Solid
                          tag={Link}
                          buttonClass="secondary"
                          to={`/promotions/duplicate/${record.id}`}
                          // icon={<IconCopy />}
                          data-action="duplicate"
                          disabled={record.status === 'deleted'}
                          onClick={() =>
                            updateLockStore.getState().lockUpdate()
                          }
                        >
                          {t('common.duplicate')}
                        </Button.Solid>
                        {record.status !== 'enabled' && (
                          <Button.Solid
                            icon={<IconDeleteTrash />}
                            data-action="delete"
                            onClick={() => {
                              updateLockStore.getState().lockUpdate();
                              setPromotionToBeDeleted(record.id);
                            }}
                          >
                            {t('common.delete')}
                          </Button.Solid>
                        )}
                      </div>
                    );
                  },
                },
              ]}
              pagination={false}
            />
            <div className={classes.pagination}>
              <Pagination
                total={promotions?.meta.pagination.total}
                pageSize={promotions?.meta.pagination.pageSize}
                current={promotions?.meta.pagination.page}
                hideOnSinglePage
                showSizeChanger={false}
                onChange={(p) => {
                  navigate({
                    to: '/promotions',
                    search: {
                      p,
                    },
                  });
                }}
              />
            </div>
          </div>
        </>
      )}

      {prmotionToBeDeleted !== undefined && (
        <PromotionDeleteModal
          promotionId={prmotionToBeDeleted}
          onClose={() => {
            updateLockStore.getState().unlockUpdate();
            setPromotionToBeDeleted(undefined);
          }}
        />
      )}
    </>
  );
}

export const Route = createFileRoute('/_main-ui/_protected/promotions/')({
  component: PromotionPage,
  validateSearch: type({
    'p?': type.string
      .or(type.number)
      .or(type.undefined)
      .pipe((s) =>
        match(s)
          .with(P.number, (x) => Math.max(1, x))
          .with(P.string, (x) => Math.max(1, ~~x))
          .otherwise(() => 1)
      ),
  }),
});
